import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function Pagos() {
  const { usuario } = useAuth();
  const [pagos, setPagos] = useState([]);
  const [fincas, setFincas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtroFinca, setFiltroFinca] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({
    finca_id: '', monto: '', metodo: 'transferencia',
    referencia: '', notas: '',
  });
  const [guardando, setGuardando] = useState(false);

  // Ver comprobante
  const [verComprobante, setVerComprobante] = useState(null);
  // Rechazar
  const [rechazando, setRechazando] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');

  if (usuario && usuario.rol !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [pagosRes, fincasRes] = await Promise.all([
        api.get('/pagos'),
        api.get('/fincas'),
      ]);
      setPagos(pagosRes.data || []);
      setFincas(fincasRes.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const kpis = useMemo(() => {
    let totalMes = 0, totalAnio = 0, total = 0;
    let porVerificar = 0;
    const hoy = new Date();
    for (const p of pagos) {
      if (p.estado === 'en_revision' || p.estado === 'pendiente') porVerificar++;
      if (p.estado !== 'completado') continue;
      const monto = Number(p.monto);
      const fecha = new Date(p.fecha_pago);
      total += monto;
      if (fecha.getFullYear() === hoy.getFullYear()) totalAnio += monto;
      if (fecha.getFullYear() === hoy.getFullYear() && fecha.getMonth() === hoy.getMonth()) {
        totalMes += monto;
      }
    }
    return { total, totalMes, totalAnio, cantidad: pagos.length, porVerificar };
  }, [pagos]);

  const pagosFiltrados = useMemo(() => {
    let lista = [...pagos];
    if (filtroFinca) lista = lista.filter((p) => p.finca_id === filtroFinca);
    if (filtroEstado) lista = lista.filter((p) => p.estado === filtroEstado);
    if (filtroDesde) lista = lista.filter((p) => new Date(p.fecha_pago) >= new Date(filtroDesde));
    if (filtroHasta) lista = lista.filter((p) => new Date(p.fecha_pago) <= new Date(filtroHasta + 'T23:59:59'));
    return lista;
  }, [pagos, filtroFinca, filtroEstado, filtroDesde, filtroHasta]);

  const badgeMetodo = (m) => {
    const colores = {
      payphone: 'bg-purple-100 text-purple-800',
      paypal: 'bg-blue-100 text-blue-800',
      transferencia: 'bg-green-100 text-green-800',
      efectivo: 'bg-amber-100 text-amber-800',
      otro: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${colores[m] || colores.otro}`}>
        {m.toUpperCase()}
      </span>
    );
  };

  const badgeEstado = (e) => {
    const colores = {
      en_revision: 'bg-amber-100 text-amber-800',
      pendiente: 'bg-blue-100 text-blue-800',
      completado: 'bg-green-100 text-green-800',
      fallido: 'bg-red-100 text-red-800',
      reembolsado: 'bg-gray-100 text-gray-600',
    };
    const labels = {
      en_revision: 'EN REVISIÓN',
      pendiente: 'PENDIENTE',
      completado: 'COMPLETADO',
      fallido: 'RECHAZADO',
      reembolsado: 'REEMBOLSADO',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${colores[e] || 'bg-gray-100'}`}>
        {labels[e] || e}
      </span>
    );
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.post('/pagos', { ...form, monto: Number(form.monto) });
      setModal(false);
      setForm({ finca_id: '', monto: '', metodo: 'transferencia', referencia: '', notas: '' });
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al registrar');
    } finally {
      setGuardando(false);
    }
  };

  const aprobar = async (p) => {
    const ok = confirm(
      `¿APROBAR el pago de $${Number(p.monto).toFixed(2)} de "${p.finca_nombre}"?\n\n` +
      `La suscripción se extenderá 1 mes automáticamente.`
    );
    if (!ok) return;
    try {
      const { data } = await api.put(`/pagos/${p.id}/aprobar`, { ciclo: 'mensual' });
      alert(`✅ ${data.mensaje}`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al aprobar');
    }
  };

  const rechazar = async () => {
    if (!rechazando) return;
    if (motivoRechazo.trim().length < 3) {
      alert('Debes indicar un motivo');
      return;
    }
    try {
      await api.put(`/pagos/${rechazando.id}/rechazar`, { motivo: motivoRechazo.trim() });
      setRechazando(null);
      setMotivoRechazo('');
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al rechazar');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-700">Pagos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Historial de facturación y verificación de comprobantes.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={cargar}
            className="px-4 py-2 border border-slate-400 text-slate-700 rounded-lg hover:bg-slate-100 text-sm"
          >
            🔄 Actualizar
          </button>
          <button
            onClick={() => setModal(true)}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium"
          >
            + Registrar pago
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="text-xs text-gray-500">Mes actual</div>
          <div className="text-2xl font-bold text-slate-700">${kpis.totalMes.toFixed(2)}</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="text-xs text-gray-500">Año actual</div>
          <div className="text-2xl font-bold text-slate-700">${kpis.totalAnio.toFixed(2)}</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="text-xs text-gray-500">Total histórico</div>
          <div className="text-2xl font-bold text-slate-700">${kpis.total.toFixed(2)}</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="text-xs text-gray-500">Pagos registrados</div>
          <div className="text-2xl font-bold text-slate-700">{kpis.cantidad}</div>
        </div>
        <button
          onClick={() => setFiltroEstado(filtroEstado === 'en_revision' ? '' : 'en_revision')}
          className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtroEstado === 'en_revision' ? 'border-amber-500' : 'border-transparent'}`}
        >
          <div className="text-xs text-gray-500">Por verificar</div>
          <div className="text-2xl font-bold text-amber-600">{kpis.porVerificar}</div>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
        <label className="block">
          <span className="text-xs text-gray-600">Finca</span>
          <select
            value={filtroFinca}
            onChange={(e) => setFiltroFinca(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Todas</option>
            {fincas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Estado</span>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Todos</option>
            <option value="en_revision">En revisión</option>
            <option value="pendiente">Pendiente</option>
            <option value="completado">Completado</option>
            <option value="fallido">Rechazado</option>
            <option value="reembolsado">Reembolsado</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Desde</span>
          <input
            type="date" value={filtroDesde}
            onChange={(e) => setFiltroDesde(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Hasta</span>
          <input
            type="date" value={filtroHasta}
            onChange={(e) => setFiltroHasta(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
          />
        </label>
        <div className="flex items-end">
          <button
            onClick={() => { setFiltroFinca(''); setFiltroEstado(''); setFiltroDesde(''); setFiltroHasta(''); }}
            className="w-full px-3 py-2 border rounded-lg text-sm hover:bg-gray-100"
          >
            Limpiar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {cargando ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : pagosFiltrados.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {pagos.length === 0 ? 'Aún no hay pagos registrados.' : 'No hay pagos con ese filtro.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                  <th className="text-left px-4 py-3 font-semibold">Finca</th>
                  <th className="text-left px-4 py-3 font-semibold">Método</th>
                  <th className="text-left px-4 py-3 font-semibold">Referencia</th>
                  <th className="text-right px-4 py-3 font-semibold">Monto</th>
                  <th className="text-center px-4 py-3 font-semibold">Estado</th>
                  <th className="text-right px-4 py-3 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagosFiltrados.map((p) => {
                  const enRevision = p.estado === 'en_revision' || p.estado === 'pendiente';
                  return (
                    <tr key={p.id} className={`border-t hover:bg-slate-50 ${enRevision ? 'bg-amber-50/50' : ''}`}>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {format(new Date(p.fecha_pago), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {p.finca_nombre || '—'}
                      </td>
                      <td className="px-4 py-3">{badgeMetodo(p.metodo)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 font-mono">
                        {p.referencia || '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        ${Number(p.monto).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">{badgeEstado(p.estado)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {p.comprobante_base64 && (
                          <button
                            onClick={() => setVerComprobante(p)}
                            className="text-xs px-2 py-1 text-slate-700 border border-slate-300 rounded hover:bg-slate-100 mr-1"
                            title="Ver comprobante"
                          >
                            👁
                          </button>
                        )}
                        {enRevision && (
                          <>
                            <button
                              onClick={() => aprobar(p)}
                              className="text-xs px-2 py-1 text-green-700 border border-green-300 rounded hover:bg-green-50 mr-1"
                              title="Aprobar y renovar 1 mes"
                            >
                              ✓ Aprobar
                            </button>
                            <button
                              onClick={() => { setRechazando(p); setMotivoRechazo(''); }}
                              className="text-xs px-2 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50"
                              title="Rechazar"
                            >
                              ✕
                            </button>
                          </>
                        )}
                        {p.estado === 'fallido' && p.motivo_rechazo && (
                          <span
                            className="text-[10px] text-red-600 italic"
                            title={p.motivo_rechazo}
                          >
                            Motivo
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal ver comprobante */}
      {verComprobante && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setVerComprobante(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 bg-slate-700 text-white flex justify-between items-center">
              <div>
                <h2 className="text-base font-bold">Comprobante de pago</h2>
                <p className="text-xs text-slate-300">
                  {verComprobante.finca_nombre} · ${Number(verComprobante.monto).toFixed(2)}
                </p>
              </div>
              <button onClick={() => setVerComprobante(null)} className="w-8 h-8 rounded-full hover:bg-white/10">✕</button>
            </div>
            <div className="p-4 overflow-auto flex-1 bg-slate-100 flex justify-center items-center">
              {verComprobante.comprobante_base64?.startsWith('data:application/pdf') ? (
                <iframe
                  src={verComprobante.comprobante_base64}
                  title="Comprobante PDF"
                  className="w-full"
                  style={{ height: '70vh' }}
                />
              ) : (
                <img
                  src={verComprobante.comprobante_base64}
                  alt="Comprobante"
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal rechazar */}
      {rechazando && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => setRechazando(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 bg-red-600 text-white flex justify-between items-center rounded-t-2xl">
              <h2 className="text-base font-bold">Rechazar pago</h2>
              <button onClick={() => setRechazando(null)} className="w-8 h-8 rounded-full hover:bg-white/10">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div><strong>{rechazando.finca_nombre}</strong></div>
                <div className="text-gray-500">${Number(rechazando.monto).toFixed(2)} · {rechazando.referencia || 'sin referencia'}</div>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Motivo del rechazo *</span>
                <textarea
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  rows={3}
                  placeholder="Ej: el comprobante no corresponde al monto indicado"
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  onClick={() => setRechazando(null)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={rechazar}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  Rechazar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal registrar pago manual */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => !guardando && setModal(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 bg-slate-700 text-white flex justify-between items-center rounded-t-2xl">
              <h2 className="text-base font-bold">Registrar pago</h2>
              <button onClick={() => !guardando && setModal(false)} className="w-8 h-8 rounded-full hover:bg-white/10">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Finca *</span>
                <select
                  value={form.finca_id}
                  onChange={(e) => setForm((f) => ({ ...f, finca_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">— Seleccionar —</option>
                  {fincas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Monto (USD) *</span>
                <input
                  type="number" step="0.01" min="0"
                  value={form.monto}
                  onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Método *</span>
                <select
                  value={form.metodo}
                  onChange={(e) => setForm((f) => ({ ...f, metodo: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="transferencia">Transferencia bancaria</option>
                  <option value="payphone">QR DeUna / Payphone</option>
                  <option value="paypal">PayPal</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="otro">Otro</option>
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Referencia</span>
                <input
                  type="text"
                  value={form.referencia}
                  onChange={(e) => setForm((f) => ({ ...f, referencia: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Notas</span>
                <textarea
                  value={form.notas}
                  onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  onClick={() => !guardando && setModal(false)}
                  disabled={guardando}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardar}
                  disabled={guardando || !form.finca_id || !form.monto}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  {guardando ? 'Guardando...' : 'Registrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}